import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Alert, Box, Button, Chip, MenuItem, Paper, Stack, Step, StepLabel, Stepper, TextField, Typography,
} from '@mui/material';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import PageLayout from '../shell/PageLayout';
import useDraft from '../common/util/useDraft';
import FormRow from '../common/components/FormRow';
import { request, useOperations } from './OperationsProvider';
import useOperationalPermissions from '../common/util/operationalPermissions';

const steps = ['Vehicle details', 'Owner & driver', 'Documents', 'RFID & GPS', 'Review'];
const documentKinds = ['Registration', 'Insurance', 'PUC', 'Permit'];
const VehicleRegistrationPage = () => {
  const navigate = useNavigate(); const { data, refresh } = useOperations(); const { administer, operate, haulier } = useOperationalPermissions();
  const fleetWide = useSelector((state) => (state.session.user?.vehicleIds || []).some((id) => String(id) === '*'));
  const [step, setStep] = useState(0); const [pending, setPending] = useState(false); const [error, setError] = useState('');
  const [form, setForm, discardForm] = useDraft('registration', {
    name: '', vehicleType: 'Truck', owner: '', transporter: '', driverName: '', driverMobile: '', rfid: '', gps: '', providerId: '', rfidAssignmentMode: 'PERMANENT', assignmentMode: 'TEMPORARY', photoDataUrl: '',
  });
  const [documentDraft, setDocumentDraft, discardDocuments] = useDraft('registration.documents', { rows: documentKinds.map((kind) => ({ kind, reference: '', expiresOn: '' })) });
  const documents = documentDraft.rows;
  const setDocuments = (value) => setDocumentDraft((current) => ({ rows: typeof value === 'function' ? value(current.rows) : value }));
  const [query, setQuery] = useState(''); const [results, setResults] = useState(null);
  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const field = (key, label, required = true, options = undefined) => <TextField fullWidth label={label} required={required} value={form[key]} onChange={(e) => set(key, e.target.value)} select={!!options} inputProps={{ maxLength: 100 }}>{options?.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}</TextField>;
  const lookup = async () => { setPending(true); setError(''); setResults(null); try { setResults(await request(`/api/operations/vehicle-lookup?q=${encodeURIComponent(query.trim())}`)); } catch (e) { setError(e.message); } finally { setPending(false); } };
  const photo = (event) => {
    const file = event.target.files[0]; if (!file) return;
    if (!['image/png', 'image/jpeg'].includes(file.type) || file.size > 200 * 1024) { setError('Choose a PNG or JPEG photo up to 200 KB.'); return; }
    const reader = new FileReader(); reader.onload = () => { set('photoDataUrl', reader.result); setError(''); }; reader.onerror = () => setError('Could not read the photo.'); reader.readAsDataURL(file);
  };
  const next = async (event) => {
    event.preventDefault(); setError('');
    if (step === 2 && documents.some((d) => !!d.reference !== !!d.expiresOn)) { setError('Provide both the reference and expiry date for each document you add.'); return; }
    if (step < 4) { setStep(step + 1); return; }
    setPending(true);
    try { await request('/api/operations/vehicle-registration', 'POST', { ...form, documents: documents.filter((d) => d.reference) }); await refresh(); discardForm(); discardDocuments(); navigate('/vehicles'); } catch (e) { setError(e.message); } finally { setPending(false); }
  };
  if (!operate && !haulier) return <PageLayout title="Vehicle Registration"><Alert severity="info">A plant operator, tenant administrator or transporter can register vehicles.</Alert></PageLayout>;
  if (!fleetWide) return <PageLayout title="Vehicle Registration"><Alert severity="info">Registering a vehicle searches and adds to the whole fleet, so it needs access to all vehicles. Ask a tenant administrator.</Alert></PageLayout>;
  return (
    <PageLayout title="Vehicle Registration" subtitle="Build a complete vehicle record, ready for gate and tracking operations.">
      <Button onClick={() => { discardForm(); discardDocuments(); setStep(0); }}>Discard saved draft</Button>
      <Typography variant="caption" component="p">Text is recovered in this tab for 24 hours. Photos must be reattached after reload.</Typography>
      <Stepper activeStep={step} alternativeLabel sx={{ mb: 4, overflowX: 'auto' }}>{steps.map((s) => <Step key={s}><StepLabel>{s}</StepLabel></Step>)}</Stepper>
      <Box component="form" onSubmit={next} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 280px' }, gap: 3 }}>
        <Paper variant="outlined" sx={{ p: { xs: 2, md: 4 } }}>
          <Stack gap={2.5}>
            <Typography variant="h6">{steps[step]}</Typography>
            {error && <Alert severity="error">{error}</Alert>}
            {step === 0 && (
            <>
              <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f5f9fc' }}>
                <Stack gap={2}>
                  <Typography fontWeight={600}>Search connected VTS</Typography>
                  <Stack direction="row" gap={1}>
                    <TextField fullWidth label="Vehicle number or GPS identifier" value={query} onChange={(e) => setQuery(e.target.value)} />
                    <Button disabled={pending || query.trim().length < 3} onClick={lookup}>Search VTS</Button>
                  </Stack>
                  {results?.length === 0 && <Typography>No matching vehicle in your approved VTS devices. Enter details below.</Typography>}
                  {results?.map((r) => <Button key={r.providerId} variant="outlined" onClick={() => { setForm((f) => ({ ...f, ...r })); setResults(null); }}>{`${r.name} · ${r.gps} — Use vehicle`}</Button>)}
                </Stack>
              </Paper>
              <FormRow>
                {field('name', 'Vehicle number')}
                {field('vehicleType', 'Vehicle type', true, ['Truck', 'Trailer', 'Tanker', 'Van', 'Other'])}
              </FormRow>
            </>
            )}
            {step === 1 && (
            <>
              <FormRow>
                {field('owner', 'Owner / company')}
                {field('transporter', 'Transporter')}
              </FormRow>
              {/* Linking the truck to a registered transporter is what lets that transporter keep its
                  own paperwork current; the free-text name above stays for what appears on a challan. */}
              <TextField select fullWidth label="Registered transporter (optional)" value={form.transporterId || ''} onChange={(e) => setForm((f) => ({ ...f, transporterId: e.target.value }))}>
                <MenuItem value="">Not linked</MenuItem>
                {(data.transporters || []).map((t) => <MenuItem key={t.id} value={String(t.id)}>{t.name}</MenuItem>)}
              </TextField>
              <FormRow>
                {field('driverName', 'Driver name')}
                {field('driverMobile', 'Driver mobile')}
              </FormRow>
            </>
            )}
            {step === 2 && (
            <>
              <Typography color="text.secondary">Record document references and validity dates. Expiry is checked against the date; documents are not externally verified.</Typography>
              {documents.map((doc, index) => (
                <Stack key={doc.kind} direction={{ xs: 'column', sm: 'row' }} gap={2}>
                  <TextField fullWidth label={`${doc.kind} reference`} value={doc.reference} required={!!doc.expiresOn} onChange={(e) => setDocuments((ds) => ds.map((d, i) => (i === index ? { ...d, reference: e.target.value } : d)))} inputProps={{ maxLength: 100 }} />
                  <TextField fullWidth label="Valid until" type="date" InputLabelProps={{ shrink: true }} value={doc.expiresOn} required={!!doc.reference} onChange={(e) => setDocuments((ds) => ds.map((d, i) => (i === index ? { ...d, expiresOn: e.target.value } : d)))} />
                </Stack>
              ))}
            </>
            )}
            {step === 3 && (
            <>
              {field('rfid', 'RFID tag identifier', false)}
              {form.rfid && field('rfidAssignmentMode', 'RFID tag assignment', true, ['PERMANENT', 'TEMPORARY'])}
              {field('gps', 'GPS device identifier', false)}
              {field('providerId', 'Approved VTS device ID', false)}
              {form.gps && !haulier && field('assignmentMode', 'GPS assignment', true, administer ? ['TEMPORARY', 'PERMANENT'] : ['TEMPORARY'])}
              {form.gps && haulier && <Alert severity="info">Your own fitted unit is attached permanently, so the truck reports whether or not it is working at the plant.</Alert>}
              <Alert severity="info">Choose Permanent for a tag fixed to the truck. A temporary tag or GPS device is released when its trip closes and must be returned before it is issued again.</Alert>
              {!administer && <Alert severity="info">You can attach RFID tags and GPS devices that are already registered. A tenant administrator registers new devices and makes GPS devices permanent.</Alert>}
              <Alert severity="info">Enter a tag manually or with a keyboard-style RFID scanner. GPS reception requires an approved VTS device mapping. You can register without a source and assign one later.</Alert>
            </>
            )}
            {step === 4 && (
            <>
              {Object.entries({
                Vehicle: form.name, Type: form.vehicleType, Owner: form.owner, Transporter: form.transporter, Driver: form.driverName, Mobile: form.driverMobile, RFID: form.rfid ? `${form.rfid} · ${form.rfidAssignmentMode}` : 'Not assigned', GPS: form.gps ? `${form.gps} · ${form.assignmentMode}` : 'Not assigned', 'VTS reference': form.providerId || 'Not mapped',
              }).map(([k, v]) => (
                <Stack direction="row" justifyContent="space-between" gap={2} key={k}>
                  <Typography color="text.secondary">{k}</Typography>
                  <Typography fontWeight={600}>{v}</Typography>
                </Stack>
              ))}
              {documents.filter((d) => d.reference).map((d) => (
                <Stack key={d.kind} direction="row" justifyContent="space-between">
                  <Typography>{`${d.kind} · ${d.reference}`}</Typography>
                  <Chip size="small" color={new Date(`${d.expiresOn}T23:59:59`) < new Date() ? 'error' : 'success'} label={`Expires ${d.expiresOn}`} />
                </Stack>
              ))}
              {!documents.some((d) => d.reference) && <Alert severity="info">No document records added.</Alert>}
            </>
            )}
            <Stack direction="row" justifyContent="space-between" sx={{ pt: 2 }}>
              <Button disabled={pending} onClick={() => (step ? setStep(step - 1) : navigate('/vehicles'))}>{step ? 'Back' : 'Cancel'}</Button>
              <Button type="submit" variant="contained" disabled={pending}>{pending ? 'Working…' : step === 4 ? 'Register vehicle' : 'Next'}</Button>
            </Stack>
          </Stack>
        </Paper>
        <Paper variant="outlined" sx={{ p: 3, alignSelf: 'start' }}>
          <Stack gap={2} alignItems="center">
            {form.photoDataUrl ? <Box component="img" src={form.photoDataUrl} alt="Vehicle preview" sx={{ width: '100%', height: 170, objectFit: 'contain', borderRadius: 2 }} /> : <LocalShippingIcon sx={{ fontSize: 90, color: '#9fb5c3', my: 4 }} />}
            <Typography variant="h6">{form.name || 'New vehicle'}</Typography>
            <Chip label={form.vehicleType} />
            <Button component="label" variant="outlined">
              {form.photoDataUrl ? 'Change photo' : 'Upload photo'}
              <input type="file" hidden accept="image/png,image/jpeg" onChange={photo} />
            </Button>
            {form.photoDataUrl && <Button onClick={() => set('photoDataUrl', '')}>Remove photo</Button>}
            <Typography variant="caption" color="text.secondary">PNG / JPEG · Up to 200 KB</Typography>
          </Stack>
        </Paper>
      </Box>
    </PageLayout>
  );
};
export default VehicleRegistrationPage;
